import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { ImpersonationSession, ImpersonationSessionsQuery } from '@niko-crm/ui';
import { ImpersonationSessionActions } from './impersonation-session.actions';

export interface ImpersonationSessionState extends EntityState<ImpersonationSession> {
  total: number;
  /** Last query sent — the page reloads it verbatim after a revoke. */
  query: ImpersonationSessionsQuery;
  loading: boolean;
  /** Id of the row whose revoke is in flight, to disable only that button. */
  revokingId: string | null;
  error: string | null;
}

export const impersonationSessionAdapter: EntityAdapter<ImpersonationSession> =
  createEntityAdapter<ImpersonationSession>();

const initialState: ImpersonationSessionState = impersonationSessionAdapter.getInitialState({
  total: 0,
  query: { page: 1, limit: 20 },
  loading: false,
  revokingId: null,
  error: null,
});

export const impersonationSessionReducer = createReducer(
  initialState,
  on(ImpersonationSessionActions.loadSessions, (state, { query }) => ({
    ...state,
    query,
    loading: true,
    error: null,
  })),
  on(ImpersonationSessionActions.loadSessionsSuccess, (state, { result }) =>
    impersonationSessionAdapter.setAll(result.data, {
      ...state,
      total: result.total,
      query: { ...state.query, page: result.page, limit: result.limit },
      loading: false,
    }),
  ),
  on(ImpersonationSessionActions.loadSessionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ImpersonationSessionActions.revokeSession, (state, { id }) => ({
    ...state,
    revokingId: id,
    error: null,
  })),
  // The row is not patched locally: the effect reloads the current page so
  // `active` / `revokedAt` come from the backend, the only place that derives them.
  on(ImpersonationSessionActions.revokeSessionSuccess, (state) => ({
    ...state,
    revokingId: null,
  })),
  on(ImpersonationSessionActions.revokeSessionFailure, (state, { error }) => ({
    ...state,
    revokingId: null,
    error,
  })),
);

export const impersonationSessionsFeature = createFeature({
  name: 'adminImpersonationSessions',
  reducer: impersonationSessionReducer,
});
